import classNames from "classnames";
import dayjs from "dayjs";
import * as React from "react";
import { Legend } from "./Fieldset";
import { HelpText, HelpTextProps } from "./HelpText";
import { ValidationError } from "./ValidationError";

// const CURRENT_DATE = new Date();
const CURRENT_DATE = dayjs();
const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const DAYS = Array.from(Array(31).keys()).map((v) => v + 1);

interface DateFieldProps {
  id: string;
  name: string;
  label: string;
  optional?: boolean;
  helpText?: HelpTextProps["helpText"];
  value: string;
  error?: string;
  touched?: boolean;
  onChange: (date: dayjs.Dayjs) => void;
  onBlur: (e: React.FocusEvent<HTMLSelectElement>) => void;
  previousDate?: dayjs.Dayjs | null;
  maxDate: dayjs.Dayjs;
  disabled?: boolean;
}

type DateFieldForwardedRef = null | {
  fieldsetRef: HTMLFieldSetElement;
  dayInputRef: HTMLSelectElement;
  monthInputRef: HTMLSelectElement;
  yearInputRef: HTMLSelectElement;
};

export const DateField = React.forwardRef<
  DateFieldForwardedRef,
  DateFieldProps
>(
  (
    {
      id,
      name,
      value,
      error,
      touched,
      optional,
      helpText,
      maxDate,
      onChange,
      onBlur,
      label,
      previousDate,
      disabled = false,
    },
    ref
  ) => {
    const fieldSetRef = React.useRef<HTMLFieldSetElement>(null);
    const dayInputRef = React.useRef<HTMLSelectElement>(null);
    const monthInputRef = React.useRef<HTMLSelectElement>(null);
    const yearInputRef = React.useRef<HTMLSelectElement>(null);

    React.useImperativeHandle(ref, () => ({
      fieldsetRef: fieldSetRef.current,
      dayInputRef: dayInputRef.current,
      monthInputRef: monthInputRef.current,
      yearInputRef: yearInputRef.current,
    }));

    const $onChange = React.useRef(onChange);
    const [date, setDate] = React.useState<dayjs.Dayjs | null>(() => {
      if (value) {
        return dayjs(value);
      }

      if (value === null) {
        return null;
      }

      return dayjs();
    });

    const { day, month, year, isoDate } = React.useMemo(() => {
      if (date === null) {
        return {
          day: 0,
          month: -1,
          year: 0,
          isoDate: "",
        };
      }

      return {
        day: date.date(),
        month: date.month(),
        year: date.year(),
        isoDate: date.toISOString(),
      };
    }, [date]);

    const handleDayChange = (e) => {
      const value = parseInt(e.target.value, 10);
      const _date = date || dayjs();

      setDate(value ? _date.set("date", value) : null);
    };

    const handleMonthChange = (e) => {
      const value = parseInt(e.target.value, 10);
      const _date = date || dayjs();

      // Note: January is month 0, so -1 is the value for no month selected
      setDate(value >= 0 ? _date.set("month", value) : null);
    };

    const handleYearChange = (e) => {
      const value = parseInt(e.target.value, 10);
      const _date = date || dayjs();

      setDate(value ? _date.set("year", value) : null);
    };

    React.useEffect(() => {
      $onChange.current(date);
    }, [date, $onChange]);

    /* Note - Previous year is used for Dates in previous years so it doesn't
     * default to the first item in the dropdown (i.e. the current year)
     */
    const previousYear =
      previousDate && previousDate.year() < CURRENT_DATE.year()
        ? previousDate.year()
        : null;
    const yearsList = Array.from(
      { length: maxDate.year() - CURRENT_DATE.year() + 1 },
      (_, idx) => CURRENT_DATE.year() + idx
    );

    return (
      <fieldset
        ref={fieldSetRef}
        className="form-group"
        disabled={disabled}
        aria-required={!optional}
        aria-invalid={!!error && touched}
        aria-describedby={[
          helpText ? `${id}-help-text` : null,
          error && touched ? `${id}-error-message` : null,
        ]
          .filter((x) => x != null)
          .join(" ")}
      >
        <Legend>
          {label ?? "Expiry date"}{" "}
          {optional ? <small className="text-muted">(optional)</small> : "*"}
        </Legend>
        {helpText && <HelpText id={`${id}-help-text`} helpText={helpText} />}
        <div className="mx-n2 d-flex flex-wrap">
          <div className="px-2 col-4 col-sm-auto flex-grow-1 form-group">
            <label className="sr-only" htmlFor={`${id}__day`}>
              Day
            </label>
            <select
              ref={dayInputRef}
              id={`${id}__day`}
              className={classNames("custom-select", {
                "is-invalid": !!error && touched,
              })}
              onChange={handleDayChange}
              onBlur={onBlur}
              value={day}
              aria-required={!optional}
              aria-invalid={error && touched}
              aria-describedby={[
                helpText ? `${id}-help-text` : null,
                error && touched ? `${id}-error-message` : null,
              ]
                .filter((x) => x != null)
                .join(" ")}
            >
              {optional && <option value={0}>-</option>}
              {DAYS.map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
          </div>

          <div className="px-2 col-8 col-sm-auto flex-grow-1 form-group">
            <label className="sr-only" htmlFor={`${id}__month`}>
              Month
            </label>
            <select
              ref={monthInputRef}
              id={`${id}__month`}
              className={classNames("custom-select", {
                "is-invalid": !!error && touched,
              })}
              onChange={handleMonthChange}
              onBlur={onBlur}
              value={month}
              aria-required={!optional}
              aria-invalid={error && touched}
              aria-describedby={[
                helpText ? `${id}-help-text` : null,
                error && touched ? `${id}-error-message` : null,
              ]
                .filter((x) => x != null)
                .join(" ")}
            >
              {optional && <option value={-1}>-</option>}
              {MONTHS.map((month, idx) => (
                <option key={month} value={idx}>
                  {month}
                </option>
              ))}
            </select>
          </div>

          <div className="px-2 col-12 col-sm-auto flex-grow-1 form-group">
            <label className="sr-only" htmlFor={`${id}__year`}>
              Year
            </label>

            <select
              ref={yearInputRef}
              id={`${id}__year`}
              className={classNames("custom-select", {
                "is-invalid": !!error && touched,
              })}
              onChange={handleYearChange}
              onBlur={onBlur}
              value={year}
              aria-required={!optional}
              aria-invalid={error && touched}
              aria-describedby={[
                helpText ? `${id}-help-text` : null,
                error && touched ? `${id}-error-message` : null,
              ]
                .filter((x) => x != null)
                .join(" ")}
            >
              {optional && <option value={0}>-</option>}
              {previousYear && (
                <option value={previousYear}>{previousYear}</option>
              )}
              {yearsList.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>
          <input type="hidden" name={name} value={isoDate} />
        </div>
        {error && touched && (
          <ValidationError errorId={`${id}-error-message`} message={error} />
        )}
      </fieldset>
    );
  }
);
